import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/constants";
import { todayIST } from "@/lib/reportPeriods";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, CheckCircle2, FileText, Printer } from "lucide-react";
import { LeadSearchInput } from "@/components/crm/LeadSearchInput";
import { DoctorSelect } from "@/components/crm/DoctorSelect";
import {
  InvoiceLineItems, InvoiceTotals, InvoicePayment, invoiceRow, invoiceTotals,
  toInvoiceItems, emptyPayment, paymentBody, resolvePayment,
} from "@/components/crm/InvoiceLineItems";
import { Field } from "@/pages/Leads";

/* Invoice + payment in one step, the way Practo bills: build the bill, take
 * the money (advance first, then cash/UPI/card), and print one sheet that is
 * both the invoice and the receipt. "Save without payment" is still there for
 * a bill the patient will settle later. */
export default function CreateInvoice() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const prefillPatientId = searchParams.get("patient_id") || "";
  const prefillPatientName = searchParams.get("patient_name") || "";
  const prefillService = searchParams.get("service") || "";
  const appointmentId = searchParams.get("appointment_id") || "";

  const [patientId, setPatientId] = useState(prefillPatientId);
  const [patientName, setPatientName] = useState(prefillPatientName);
  const [patientSearch, setPatientSearch] = useState(prefillPatientName);
  const [appt, setAppt] = useState(null);
  // A service handed over in the query string is the one line we already know
  // about; everything else comes off the catalogue search.
  const [rows, setRows] = useState(
    prefillService ? [invoiceRow({ description: prefillService })] : []);
  const [discount, setDiscount] = useState(0);
  const [payment, setPayment] = useState(emptyPayment);
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState("");
  // Defaults to today; set it back to enter a bill from an earlier day.
  const [invoiceDate, setInvoiceDate] = useState(todayIST);
  const [doctorId, setDoctorId] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const backdated = invoiceDate && invoiceDate < todayIST();

  // Opened from the patient's Billing tab with just an id — fetch the name.
  useEffect(() => {
    if (prefillPatientId && !prefillPatientName) {
      api.get(`/patients/${prefillPatientId}`)
        .then((r) => { setPatientName(r.data?.name || ""); setPatientSearch(r.data?.name || ""); })
        .catch(() => {});
    }
  }, [prefillPatientId, prefillPatientName]);

  useEffect(() => {
    // The visit's doctor is who the bill is for, unless the desk picks another.
    if (appointmentId) api.get(`/appointments/${appointmentId}`)
      .then((r) => { setAppt(r.data); setDoctorId((d) => d || r.data?.doctor_id || ""); })
      .catch(() => {});
  }, [appointmentId]);

  const grand = invoiceTotals(rows, discount).grand;
  const pay = resolvePayment(payment, grand);

  const submit = async (withPayment) => {
    if (!patientId) { toast.error("Select a patient"); return; }
    const items = toInvoiceItems(rows);
    if (!items.length) { toast.error("Add at least one procedure"); return; }
    if (invoiceDate > todayIST()) { toast.error("Invoice date cannot be in the future"); return; }
    setSaving(true);
    try {
      const res = await api.post("/invoices", {
        patient_id: patientId,
        appointment_id: appointmentId || null,
        branch_id: appt?.branch_id || null,
        items,
        discount: parseFloat(discount) || 0,
        notes,
        due_date: dueDate || null,
        invoice_date: invoiceDate || null,
        doctor_id: doctorId || null,
        payment: withPayment ? paymentBody(payment, grand) : null,
      });
      setDone(res.data);
      toast.success(`Invoice ${res.data.invoice_number} created`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const printPdf = (inv) => window.open(`${api.defaults.baseURL}/invoices/${inv.id}/pdf`, "_blank");

  if (done) {
    return (
      <div className="mx-auto max-w-xl space-y-5 pt-6">
        <div className="rounded-xl border border-navy/10 bg-white p-6 text-center shadow-sm">
          <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" />
          <h2 className="mt-3 font-display text-xl font-semibold text-navy">Invoice {done.invoice_number}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {formatCurrency(done.grand_total)} billed · {formatCurrency(done.amount_paid || 0)} received
            {(done.amount_due || 0) > 0 ? ` · ${formatCurrency(done.amount_due)} balance` : " · fully paid"}
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Button onClick={() => printPdf(done)} className="bg-navy text-white hover:bg-navy-hover" data-testid="invoice-print">
              <Printer className="mr-1.5 h-4 w-4" />
              {(done.amount_paid || 0) > 0 ? "Print invoice & receipt" : "Print invoice"}
            </Button>
            {patientId && (
              <Button variant="outline" onClick={() => navigate(`/patients/${patientId}`)}>Back to patient</Button>
            )}
            <Button variant="outline" onClick={() => navigate("/invoices")}>All invoices</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-8 w-8">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="font-display text-2xl font-semibold text-navy">Invoice &amp; Receipt</h2>
          <p className="text-sm text-muted-foreground">Bill the patient and take the payment in one step</p>
        </div>
      </div>

      <div className="rounded-xl border border-navy/10 bg-white p-6 shadow-sm space-y-5">
        {/* Patient */}
        <Field label="Patient *">
          {prefillPatientId ? (
            <div className="flex items-center gap-2">
              <div className="flex-1 rounded-lg bg-cream/60 px-3 py-2">
                <p className="text-sm font-semibold text-ink">{patientName || "Patient"}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setPatientId(""); setPatientSearch(""); }} className="text-xs text-muted-foreground">Change</Button>
            </div>
          ) : (
            <LeadSearchInput value={patientSearch} onChange={setPatientSearch}
              onLeadSelect={(p) => { setPatientId(p.id); setPatientName(p.name); setPatientSearch(p.name); setPayment(emptyPayment()); }}
              placeholder="Search patients by name or mobile..."
              searchEndpoint="/patients" />
          )}
        </Field>

        <Field label="Doctor (printed on the invoice and signing it)">
          <DoctorSelect value={doctorId} onChange={setDoctorId} testId="invoice-doctor" />
        </Field>

        {appt && (
          <p className="rounded-lg bg-cream/50 px-3 py-2 text-xs text-muted-foreground" data-testid="invoice-visit">
            Appointment{appt.doctor_name ? ` with ${appt.doctor_name}` : ""} on {formatDateTime(appt.scheduled_at)}
            {appt.service ? ` · ${appt.service}` : ""}
          </p>
        )}

        {/* Line Items */}
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Treatments</p>
          <InvoiceLineItems rows={rows} onChange={setRows} />
        </div>

        <InvoiceTotals rows={rows} discount={discount} onDiscount={setDiscount} />

        {patientId && grand > 0 && (
          <InvoicePayment patientId={patientId} grand={grand} value={payment} onChange={setPayment} />
        )}

        {/* Invoice date, due date & notes */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Invoice Date">
            <Input type="date" value={invoiceDate} max={todayIST()} data-testid="invoice-date"
                   onChange={(e) => setInvoiceDate(e.target.value)} />
          </Field>
          <Field label="Due Date">
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
          <Field label="Notes">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" />
          </Field>
        </div>
        {/* The server dates this step's payment to the bill date too, so say so —
            if the patient is paying today, save without payment and record it after. */}
        {backdated && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="invoice-backdated-note">
            Backdated to {formatDate(invoiceDate)}. A payment accepted here is recorded on that date too.
            If the patient is paying today, save without payment and record the payment separately.
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap justify-end gap-3">
        <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="outline" onClick={() => submit(false)} disabled={saving} data-testid="invoice-save-unpaid">
          <FileText className="mr-1.5 h-4 w-4" /> Save without payment
        </Button>
        <Button onClick={() => submit(true)} disabled={saving || !(pay.collected > 0)}
                className="bg-gold text-white hover:bg-gold/90" data-testid="invoice-accept-payment">
          {saving ? "Saving…" : `Accept Payment ₹${pay.collected.toLocaleString("en-IN")}`}
        </Button>
      </div>
    </div>
  );
}
